/**
 * Client-side reference counting for API-Gateway tournament subscriptions
 * (`subscribeTournament` / `unsubscribeTournament`, which drive the per-player
 * `gotTnmStatus` feed).
 *
 * WHY: the tournament feed used to be subscribed from whichever component
 * happened to be mounted — TournamentScreenModal, TournamentDetails, the lobby
 * panel — each firing its own subscribe/unsubscribe pair. Two problems fell out:
 *
 *  1. Concurrent subscribers. On desktop the single-table panel AND every grid
 *     cell stay mounted (SlideContainer), so a seated tournament table had 2-4
 *     components each emitting subscribe/unsubscribe for the SAME tournament —
 *     the observed burst of 4 rapid `unsubscribeTournament` on a table move.
 *  2. A dropped subscribe drops the feed for the whole connection. Because
 *     `unsubscribeTournament` removes the connection from the tournament's
 *     fan-out entirely, ANY stray unsubscribe (e.g. an unmounting modal, or the
 *     empty-string cleanup bug) killed `gotTnmStatus` for every other subscriber
 *     of that tournament on the same connection.
 *
 * Ref-counting collapses all of that: the real socket emit happens only on the
 * 0->1 (first subscribe) and 1->0 (last unsubscribe) transitions. The last
 * unsubscribe re-emits the SAME id value the first subscribe used, so the
 * subscribe/unsubscribe pair is always type-consistent server-side (the raw
 * callers mix `number` and `string` tournamentIds).
 *
 * An empty / falsy tournamentId is ignored entirely (never emitted, never
 * counted) — this also neutralizes the `tournamentId:""` unsubscribe bug.
 */

type Entry = {
  count: number;
  // The exact tournamentId value the first subscribe emitted. Re-used verbatim
  // on the final unsubscribe so the server sees a matching pair even though
  // different callers pass number vs. string.
  emittedId: unknown;
};

const subs = new Map<string, Entry>();

/** Canonical key for a tournamentId (dedupes number 628 vs string "628"). */
export function normalizeTournamentId(id: unknown): string {
  if (id === null || id === undefined) return '';
  return String(id).trim();
}

/**
 * Register interest in a tournament's feed. Returns `emit: true` (with the id to
 * emit) only on the first acquire, i.e. when the caller must actually send the
 * `subscribeTournament` socket message. Falsy ids are no-ops.
 */
export function acquireTournamentSub(id: unknown): { emit: boolean; id: unknown } {
  const key = normalizeTournamentId(id);
  if (!key) return { emit: false, id };
  const existing = subs.get(key);
  if (existing) {
    existing.count += 1;
    return { emit: false, id: existing.emittedId };
  }
  subs.set(key, { count: 1, emittedId: id });
  return { emit: true, id };
}

/**
 * Release interest in a tournament's feed. Returns `emit: true` (with the id to
 * emit) only on the final release, i.e. when the caller must actually send the
 * `unsubscribeTournament` socket message. Falsy ids and over-releases are
 * no-ops.
 */
export function releaseTournamentSub(id: unknown): { emit: boolean; id: unknown } {
  const key = normalizeTournamentId(id);
  if (!key) return { emit: false, id };
  const existing = subs.get(key);
  if (!existing) return { emit: false, id };
  existing.count -= 1;
  if (existing.count <= 0) {
    subs.delete(key);
    return { emit: true, id: existing.emittedId };
  }
  return { emit: false, id: existing.emittedId };
}

/**
 * The id values of every tournament currently held (count > 0), for re-emitting
 * subscriptions after a socket reconnect WITHOUT disturbing the counts — the
 * server lost the subscriptions on the drop, but every client-side holder is
 * still holding, so counts must stay put.
 */
export function heldTournamentIds(): unknown[] {
  return Array.from(subs.values()).map((entry) => entry.emittedId);
}

/** Test-only reset of the module-level store. */
export function __resetTournamentSubs(): void {
  subs.clear();
}

type TableLike = { game?: { tabletype?: string; tournamentId?: unknown } };

/**
 * Whether a value is a real tournament id — a positive integer.
 *
 * A defensive filter for the subscribe set. Real tournamentIds are positive
 * integers (backend-assigned autoinc); anything else is either a sentinel or a
 * data error and MUST NOT be emitted onto the socket. In particular:
 *   • `-1` is truthy in JS, so the previous `if (tid)` guard let it through.
 *     `-1` is the standard "no game / not yet assigned" sentinel on the
 *     `gameID` slot — it should never appear as a tournamentId, but production
 *     logs showed it oscillating into the set (subscribe/unsubscribe every 3 s,
 *     hammering the socket + polluting the server's tournament room map).
 *   • `0`, `NaN`, floats, and stringified non-numeric values are also rejected.
 */
export function isValidTournamentId(id: unknown): boolean {
  if (id === null || id === undefined) return false;
  const n = Number(id);
  return Number.isInteger(n) && n > 0;
}

// One-time diagnostic per unique bad value per session — surfaces the source
// tag (e.g. 'tables', 'connectionManager:subscribeTournament') so we can trace
// WHY a bad id showed up. Deliberately not rate-limited by time: repeated hits
// with the same value stay silent after the first warn, but a new value warns
// immediately.
const _warnedBadTournamentIds = new Set<string>();
// Exported so other layers that guard against invalid tournamentIds (e.g. the
// ConnectionManager proxy emit) can share the same dedup key set and warn text.
// Keeping one dedup Set means opening a dev console and grepping for the tag
// gives a definitive list of every bad-id source the client has seen this
// session — reused by ConnectionManager to avoid the "silent swallow" pattern.
export function warnBadTournamentId(source: string, raw: unknown): void {
  _warnBadTournamentId(source, raw);
}
function _warnBadTournamentId(source: string, raw: unknown): void {
  const key = `${source}:${String(raw)}`;
  if (_warnedBadTournamentIds.has(key)) return;
  _warnedBadTournamentIds.add(key);
  // eslint-disable-next-line no-console
  console.warn(
    `[tournamentSubscriptions] dropping non-positive-integer tournamentId ` +
      `${JSON.stringify(raw)} from ${source} — this value must not reach ` +
      `subscribeTournament. Check upstream state population.`
  );
}

/** Test-only reset of the one-time warn dedup set. */
export function __resetTournamentIdWarnings(): void {
  _warnedBadTournamentIds.clear();
}

/**
 * The set of tournamentIds whose `gotTnmStatus` feed should be kept subscribed.
 * This is the single declarative rule the App-level centralized effect diffs on
 * — the "when to subscribe / unsubscribe" contract.
 *
 *   KEEP SUBSCRIBED when the player has an OPEN TABLE for the tournament:
 *   tabletype 'm' (MTT) or 't' (SnG). Cash tables ('s') never subscribe a
 *   tournament feed. This is intentionally the ONLY auto-subscribe source
 *   computed here — a registered-but-not-seated tournament (`playerTournament-
 *   Details` record without a corresponding table) does NOT keep the feed alive.
 *
 *   UNSUBSCRIBE when the player has no open table for the tournament. The wire
 *   emit fires immediately on the 1→0 transition of GameService's shared
 *   tournament ref-count; balancing / rebalancing don't need a deferred grace
 *   because the App-level set-based diff keeps the joined subscribe-set key
 *   stable across an in-tournament move (A and B carry the same tournamentId).
 *
 * The tournament-info panel (TournamentDetailModal / TournamentDetails) is a
 * SECOND keep-alive source, held directly by the info surfaces via the
 * ref-count (`acquireTournamentSub` / `releaseTournamentSub`), so it is
 * intentionally NOT computed here. Opening the modal subscribes; closing it
 * unsubscribes — independent of table state.
 *
 * DESIGN NOTE — dropped "Rule 1" (playerTournamentDetails-driven subscribe).
 * The prior version also held a subscription whenever a playerTournamentDetails
 * record existed with tStatus !== 'A', to deliver mid-game gotTnmStatus (HFH
 * pause, level updates, bust redirect) even in the brief windows where no
 * table happened to be open. That path was retired because it was causing
 * subscribe churn every time the record broadcaster flapped, and every
 * gameplay UI that actually needs gotTnmStatus already has a table open
 * (Rule 2 covers it). Registered-but-Announced or between-tables states no
 * longer subscribe until an open table backs them.
 *
 * Returned SORTED so callers can `.join(',')` into a stable key and only re-run
 * their effect when the SET actually changes — not on every per-table frame.
 */
export function computeSubscribedTournamentIds(
  tables: Record<string, TableLike> | null | undefined
): string[] {
  const ids = new Set<string>();
  for (const t of Object.values(tables || {})) {
    const tt = t?.game?.tabletype;
    const rawTid = t?.game?.tournamentId;
    if (tt !== 'm' && tt !== 't') continue;
    if (rawTid === null || rawTid === undefined || rawTid === 0 || rawTid === '') continue;
    if (!isValidTournamentId(rawTid)) {
      _warnBadTournamentId('tables', rawTid);
      continue;
    }
    ids.add(String(rawTid));
  }
  return Array.from(ids).sort();
}

/**
 * Narrow the "tables" map to only the ones the player is CURRENTLY VIEWING, so
 * the subscribe set follows what's on screen rather than what's loaded in
 * memory.
 *
 * Rules (product spec):
 *   • lobby  → no tables visible. Even a backgrounded tournament table stops
 *              contributing to the subscribe set. Navigating to the lobby
 *              drops the tournament subscriptions IMMEDIATELY; returning to
 *              the table re-subscribes. (The prior 30 s deferred-teardown
 *              "grace" was removed after this file's set-based diff was in
 *              place — in-tournament rebalancing keeps the joined key stable
 *              across A → B moves and no longer needs the grace to survive a
 *              transient count-zero window.)
 *   • table  → only the URL-focused single table is visible. Other tables the
 *              player has in the background do NOT keep their tournaments
 *              subscribed.
 *   • grid   → every table in the grid is visible; the map is passed through
 *              unchanged so all grid-cell tournaments stay subscribed.
 *
 * NOTE: tournament-info-panel subscriptions are ref-counted separately (see
 * acquireTournamentSub / releaseTournamentSub) and are unaffected — opening
 * the info modal from the lobby holds the feed independently of the tables
 * visible here.
 */
export function visibleTablesForActiveView(
  activeView: 'lobby' | 'table' | 'grid',
  focusedGameId: string,
  allTables: Record<string, TableLike> | null | undefined
): Record<string, TableLike> {
  if (!allTables) return {};
  if (activeView === 'lobby') return {};
  if (activeView === 'grid') return allTables;
  // activeView === 'table' — narrow to the URL-focused cell only.
  if (!focusedGameId) return {};
  const focused = allTables[focusedGameId];
  return focused ? { [focusedGameId]: focused } : {};
}
